import { setLoaded, setLoading, withCallState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { patchState, signalMethod, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Compartment, GetApiZone, Greenhouse, Guid, Row, SpatialStore } from '@privaone/masterdata';
import { distinctUntilChanged, pipe, tap } from 'rxjs';

import { AuthStore, SitesStore } from 'app/core/stores';

import { SiteTopology } from '../../models';
import {
    extractItemsFromParent,
    findItemsById,
    handleError,
    isSameTopologySelection,
    TopologySelectionParams,
} from '../../utils/store-helper-utils';

const TopologyCollection = 'topology';

export type TopologyStoreType = InstanceType<typeof TopologyStore>;

export type SelectedZoneState = {
    selectedSiteId: string;
    selectedGreenhouseId: string;
    selectedCompartmentId: string;
    selectedRowId?: string;
    loadedSiteIds: string[];
};

const initialSelectedZone = {
    selectedSiteId: '',
    selectedGreenhouseId: '',
    selectedCompartmentId: '',
    loadedSiteIds: [],
} as SelectedZoneState;

export const TopologyStore = signalStore(
    { providedIn: 'root' },
    withState(initialSelectedZone),
    withCallState({ collection: TopologyCollection }),
    withDevtools('TopologyStore'),
    withComputed((store, spatialStore = inject(SpatialStore)) => {
        const topologyEntities: Signal<SiteTopology[]> = computed(() => {
            const greenhouses = spatialStore.greenhouses();
            const sites = spatialStore.sites();
            return sites
                .filter((site) => store.loadedSiteIds().includes(site.id))
                .map((site) => ({
                    id: site.id,
                    name: site.name ?? '',
                    greenhouses: greenhouses.filter((greenhouse) => greenhouse.siteId === site.id),
                    number: site.number,
                }));
        });

        const topologyEntityMap: Signal<Record<Guid, SiteTopology>> = computed(() => {
            const entities = topologyEntities();
            return Object.fromEntries(entities.map((topology) => [topology.id, topology]));
        });

        const topologyIds: Signal<Guid[]> = computed(() => {
            const entities = topologyEntities();
            return entities.map((topology) => topology.id);
        });

        return { topologyEntities, topologyEntityMap, topologyIds };
    }),
    withComputed((store) => {
        const selectedSite: Signal<SiteTopology | undefined> = computed(() => {
            return findItemsById(store.topologyEntities() ?? [], store.selectedSiteId());
        });

        const greenhouses: Signal<Greenhouse[]> = computed(() => {
            return selectedSite()?.greenhouses ?? [];
        });

        const selectedGreenhouse: Signal<Greenhouse | undefined> = computed(() => {
            return findItemsById(selectedSite()?.greenhouses ?? [], store.selectedGreenhouseId());
        });

        const compartments: Signal<Compartment[]> = computed((): Compartment[] => {
            const greenhouse = selectedGreenhouse();
            if (!greenhouse) {
                return [];
            }

            return extractItemsFromParent<Greenhouse, Compartment>(greenhouse, 'compartments');
        });

        const selectedCompartment: Signal<Compartment | undefined> = computed(() => {
            const selectedCompartments = compartments();
            return findItemsById(selectedCompartments, store.selectedCompartmentId());
        });

        const rows: Signal<Row[]> = computed(() => {
            const compartment = selectedCompartment();
            if (!compartment) {
                return [];
            }
            return extractItemsFromParent<Compartment, never, Row>(compartment, 'rows');
        });

        const selectedZone: Signal<SelectedZoneState> = computed(() => {
            return {
                selectedSiteId: store.selectedSiteId(),
                selectedGreenhouseId: store.selectedGreenhouseId(),
                selectedCompartmentId: store.selectedCompartmentId(),
            } as SelectedZoneState;
        });

        const getZoneById: Signal<Map<string, GetApiZone>> = computed(() => {
            const zonePerId = new Map<string, GetApiZone>();
            store.topologyEntities().forEach((site) => {
                zonePerId.set(site.id, {
                    id: site.id,
                    siteId: site.id,
                    name: site.name,
                    number: undefined,
                    type: 'Greenhouse',
                });
                site.greenhouses.forEach((greenhouse) => {
                    zonePerId.set(greenhouse.id, {
                        id: greenhouse.id,
                        siteId: greenhouse.siteId,
                        name: greenhouse.name,
                        number: greenhouse.number,
                        type: 'Greenhouse',
                    });
                    greenhouse.compartments?.forEach((compartment) => {
                        zonePerId.set(compartment.id, {
                            id: compartment.id,
                            siteId: compartment.siteId,
                            name: compartment.name,
                            number: compartment.number,
                            type: 'Compartment',
                        });
                        compartment.rows?.forEach((row: Row) => {
                            zonePerId.set(row.id, {
                                id: row.id,
                                siteId: row.siteId,
                                name: row.name,
                                number: row.number,
                                type: 'Row',
                            });
                        });
                    });
                });
            });
            return zonePerId;
        });

        return {
            selectedSite,
            greenhouses,
            selectedGreenhouse,
            compartments,
            selectedCompartment,
            rows,
            selectedZone,
            getZoneById,
        };
    }),
    withMethods((store) => {
        const spatialStore = inject(SpatialStore);

        const authStore = inject(AuthStore);
        const sitesStore = inject(SitesStore);

        async function loadTopology(siteId: string, forceLoad: boolean = false): Promise<void> {
            if (!siteId) {
                return;
            }

            if (store.loadedSiteIds().includes(siteId) && !forceLoad) {
                return;
            }

            patchState(store, setLoading(TopologyCollection), {
                loadedSiteIds: [...store.loadedSiteIds(), siteId],
            });
            await spatialStore
                .loadSiteTopology(authStore.tenantId()!, siteId, forceLoad)
                .then(() => patchState(store, setLoaded(TopologyCollection)))
                .catch(() => handleError(store, 'ERROR.LOADING_TOPOLOGY', TopologyCollection));
        }

        const loadTopologyObserved = signalMethod<string>(async (siteId) => {
            await loadTopology(siteId);
        });

        function reloadTopology(): Promise<void> {
            return loadTopology(store.selectedSiteId(), true);
        }

        const selectTopology = rxMethod<TopologySelectionParams>(
            pipe(
                distinctUntilChanged((prev, current) => isSameTopologySelection(prev, current)),
                tap(({ siteId, greenhouseId, compartmentId }) => {
                    patchState(store, {
                        selectedSiteId: siteId,
                        selectedGreenhouseId: greenhouseId,
                        selectedCompartmentId: compartmentId,
                    });
                }),
                tap(({ siteId }) => loadTopology(siteId)),
            ),
        );

        function getLowercasedNamesOfGreenhousesAndSite(): string[] {
            const site = sitesStore.sitesEntities().find((site) => site.id === store.selectedSiteId());
            const siteName = site?.name?.toLowerCase();
            if (!siteName) {
                return [];
            }
            const greenHouseNames = store
                .greenhouses()
                .filter((greenHouse) => greenHouse?.name)
                .map((greenhouse) => greenhouse.name!.toLowerCase());
            return !greenHouseNames?.length ? [siteName] : [siteName, ...greenHouseNames];
        }

        const getAllNumbersOfSite: () => number[] = () => {
            return store
                .greenhouses()
                .flatMap((greenHouse) => getNumbersOfGreenhouse(greenHouse))
                .filter((value) => value !== undefined);
        };

        return {
            loadTopology,
            loadTopologyObserved,
            reloadTopology,
            selectTopology,
            getLowercasedNamesOfGreenhousesAndSite,
            getAllNumbersOfSite,
        };
    }),
);

function getNumbersOfGreenhouse(greenhouse: Greenhouse): number[] {
    const greenhouseNumbers: number[] = greenhouse.number !== undefined ? [greenhouse.number] : [];
    if (!greenhouse.compartments) {
        return greenhouseNumbers;
    }
    return greenhouseNumbers.concat(
        greenhouse.compartments?.flatMap((compartment) => getNumbersOfCompartment(compartment)),
    );
}

function getNumbersOfCompartment(compartment: Compartment): number[] {
    const compartmentNumbers: number[] = compartment.number !== undefined ? [compartment.number] : [];
    if (!compartment.rows?.length) {
        return compartmentNumbers;
    }
    const rowNumbers = compartment.rows.filter((row) => !!row?.number).map((subType: Row) => subType.number!);
    return compartmentNumbers.concat(rowNumbers);
}
